import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { toast } from "@/components/ui/sonner";

import { TextField } from "@/components/form/TextField";
import { useAutosaveForm } from "@/components/form/useAutosaveForm";
import { SaveStatus } from "@/components/SaveStatus";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { HistoryDrawer } from "@/components/HistoryDrawer";
import { Modal } from "@/components/ui/modal";
import { useAuth } from "@/lib/auth/auth-context";

import {
  saveGroup,
  useDeleteGroup,
  useGroupHistory,
  useGroupQuery,
} from "../api";
import { apiErrorDetail } from "../lib/errors";
import type { PartnerGroup } from "../types";
import { GroupInvitationList } from "./GroupInvitationList";
import { InviteMemberForm } from "./InviteMemberForm";
import { MemberTable } from "./MemberTable";

interface SettingsValues {
  name: string;
  /** Kept as a string for the input; converted to number|null on save. */
  max_licenses: string;
}

function SettingsForm({ group }: { group: PartnerGroup }) {
  const { t } = useTranslation();
  // A local partner's own group: the administrator sets its licences. An
  // owner with no limit of its own has no group limit to set either, beyond
  // lowering or clearing one the group still has. Read once, as the Django
  // page is drawn once: clearing the limit must not pull the field away
  // while it is being typed in.
  const [licencesEditable] = useState(
    () =>
      !group.owned_by_local_partner &&
      (group.owner_max_group_licenses != null || group.max_licenses != null),
  );

  const { form, autosave } = useAutosaveForm<SettingsValues>({
    defaultValues: {
      name: group.name ?? "",
      max_licenses: group.max_licenses == null ? "" : String(group.max_licenses),
    },
    autosave: {
      save: (values, signal) =>
        saveGroup(
          group.id,
          licencesEditable
            ? {
                name: values.name,
                max_licenses:
                  values.max_licenses.trim() === ""
                    ? null
                    : Number(values.max_licenses),
              }
            : { name: values.name },
          signal,
        ),
      debounceMs: 600,
      // Never PATCH the group into a nameless state (name is required + unique).
      shouldSave: (values) => values.name.trim() !== "",
      // Say why a save was refused (e.g. a limit the owner's pool cannot
      // cover), as the Django form does under the field.
      onError: (err) => toast.error(apiErrorDetail(err) ?? t("partners.actionFailed")),
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {group.partner_code && (
            <>
              {t("partners.detail.partnerCode")}: {group.partner_code}
              {group.owner_email && " · "}
            </>
          )}
          {group.owner_email && (
            <>
              {t("partners.detail.owner")}: {group.owner_email}
            </>
          )}
        </p>
        <SaveStatus status={autosave.status} />
      </div>
      <form
        className="grid gap-4 md:grid-cols-2"
        onSubmit={(e) => e.preventDefault()}
      >
        <form.Field name="name">
          {(field) => (
            <TextField field={field} label={t("partners.groups.nameLabel")} />
          )}
        </form.Field>
        {licencesEditable && (
          <form.Field name="max_licenses">
            {(field) => (
              <TextField
                field={field}
                type="number"
                label={t("partners.groups.maxLicensesLabel")}
                description={t("partners.groups.maxLicensesHint")}
              />
            )}
          </form.Field>
        )}
      </form>
    </div>
  );
}

/** Fill colour for a usage bar — green < 70%, amber 70–89%, red ≥ 90%. */
function poolBarClass(used: number, max: number): string {
  const pct = max > 0 ? (used / max) * 100 : 0;
  if (pct >= 90) return "bg-destructive";
  if (pct >= 70) return "bg-warning";
  return "bg-theme-economic";
}

/** A single labelled usage bar, or a plain count when the cap is unlimited. */
function PoolBar({
  label,
  used,
  max,
}: {
  label: string;
  used: number;
  max: number | null | undefined;
}) {
  const { t } = useTranslation();
  if (max == null) {
    return (
      <div className="flex items-center justify-between text-sm">
        <span className="font-semibold">{label}</span>
        <span className="tabular-nums text-muted-foreground">
          {t("partners.detail.poolUnlimited", { used })}
        </span>
      </div>
    );
  }
  const available = Math.max(0, max - used);
  const pct = max > 0 ? Math.min(100, (used / max) * 100) : 0;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <span className="font-semibold">{label}</span>
        <span className="tabular-nums text-muted-foreground">
          {t("partners.detail.poolUsedOfMax", { used, max })} ·{" "}
          {t("partners.detail.poolAvailable", { count: available })}
        </span>
      </div>
      <div className="h-3 overflow-hidden rounded-full bg-muted">
        <div
          className={`h-full rounded-full ${poolBarClass(used, max)}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/**
 * License pool, as the Django group page shows it: the owner's cross-group
 * pool (to the owner, or to anyone when the group has no limit of its own),
 * then this group's limit, or the member count when there is no limit to
 * show. A local partner's own group has no limit of its own: its pool stands
 * for it. Colour-coded at the 70/90% thresholds.
 */
function LicensePool({
  group,
  showOwnerPool,
}: {
  group: PartnerGroup;
  showOwnerPool: boolean;
}) {
  const { t } = useTranslation();
  const membersLine = (
    <p className="text-sm">
      <span className="font-semibold">{t("partners.detail.groupMembers")}</span>:{" "}
      <span className="tabular-nums">{Number(group.license_count)}</span>
    </p>
  );
  let groupPart: ReactNode = null;
  if (group.owned_by_local_partner) {
    if (!showOwnerPool) groupPart = membersLine;
  } else if (group.max_licenses != null) {
    groupPart = (
      <PoolBar
        label={t("partners.detail.poolThisGroup")}
        used={Number(group.license_count)}
        max={group.max_licenses}
      />
    );
  } else {
    groupPart = membersLine;
  }
  return (
    <div className="space-y-4">
      {showOwnerPool && (
        <PoolBar
          label={t("partners.detail.poolOwnerPool")}
          used={Number(group.owner_licenses_used ?? 0)}
          max={Number(group.owner_max_group_licenses)}
        />
      )}
      {groupPart}
    </div>
  );
}

/** Single group workspace: settings, members + license pool, and invitations. */
export function PartnerGroupDetail({
  groupId,
  onClose,
  onViewMember,
}: {
  groupId: string;
  /** Omitted for a local-partner-only user with a single group: there is no
   * list to return to, so the back control is hidden. */
  onClose?: () => void;
  onViewMember: (profileId: string, name: string) => void;
}) {
  const { t } = useTranslation();
  const group = useGroupQuery(groupId);
  // Opened from the header. Everyone who can see the group can see what
  // happened to it; the server scopes which groups that is.
  const [historyOpen, setHistoryOpen] = useState(false);
  const history = useGroupHistory(historyOpen ? groupId : null);
  const deleteGroup = useDeleteGroup();
  const { user } = useAuth();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  const remove = () => {
    if (!window.confirm(t("partners.detail.confirmDelete"))) return;
    deleteGroup.mutate(groupId, {
      onSuccess: () => onClose?.(),
      onError: (err) => toast.error(apiErrorDetail(err) ?? t("partners.actionFailed")),
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {onClose ? (
          <Button variant="ghost" size="sm" onClick={onClose}>
            <ArrowLeft />
            {t("partners.detail.back")}
          </Button>
        ) : (
          <span />
        )}
        {group.data && (
          <span className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setHistoryOpen(true)}
            >
              {t("partners.detail.history")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSettingsOpen(true)}
            >
              {t("partners.detail.settings")}
            </Button>
          </span>
        )}
      </div>

      {historyOpen && group.data && (
        <HistoryDrawer
          // `name` is optional in the generated schema; the drawer always needs
          // a heading.
          title={group.data.name ?? ""}
          onClose={() => setHistoryOpen(false)}
          query={history}
        />
      )}

      {group.data && (
        <h1 className="text-3xl font-bold">{group.data.name}</h1>
      )}

      {group.isLoading && (
        <p className="text-sm text-muted-foreground">{t("partners.loading")}</p>
      )}

      {group.isError && (
        <Card>
          <CardContent className="flex items-center justify-between gap-4 py-6">
            <p className="text-sm text-destructive">{t("partners.loadError")}</p>
            <Button variant="outline" size="sm" onClick={() => void group.refetch()}>
              {t("partners.retry")}
            </Button>
          </CardContent>
        </Card>
      )}

      {group.data && (
        <>
          {/* Always shown, as the Django group card always shows the pool,
              the group's limit or its member count. */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                {t("partners.detail.licensePool")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <LicensePool
                group={group.data}
                showOwnerPool={
                  group.data.owner_max_group_licenses != null &&
                  (group.data.owner_email === user?.email ||
                    group.data.max_licenses == null)
                }
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle className="text-lg">{t("partners.detail.members")}</CardTitle>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={() => setInviteOpen(true)}>
                  {t("partners.detail.inviteMember")}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <MemberTable groupId={groupId} onViewMember={onViewMember} />
            </CardContent>
          </Card>

          <GroupInvitationList groupId={groupId} />

          {settingsOpen && (
            <Modal
              title={t("partners.detail.settings")}
              onClose={() => setSettingsOpen(false)}
            >
              <div className="space-y-6">
                <SettingsForm key={group.data.id} group={group.data} />
                <div className="space-y-3 rounded-lg border border-destructive/40 bg-destructive/5 p-4">
                  <div>
                    <h3 className="text-sm font-semibold text-destructive">
                      {t("partners.detail.dangerZone")}
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {t("partners.detail.deleteWarning")}
                    </p>
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={deleteGroup.isPending}
                    onClick={remove}
                  >
                    {deleteGroup.isPending
                      ? t("partners.detail.deleting")
                      : t("partners.detail.delete")}
                  </Button>
                </div>
              </div>
            </Modal>
          )}

          {inviteOpen && (
            <Modal
              title={t("partners.detail.inviteMember")}
              onClose={() => setInviteOpen(false)}
            >
              <InviteMemberForm
                groupId={groupId}
                canSaveTemplate={group.data.can_set_invitation_template}
                canPickRole={group.data.can_invite_local_partners}
              />
            </Modal>
          )}

        </>
      )}
    </div>
  );
}
